import { h, empty } from '../../../../../src/shared/components/dom.js';
import { Spinner } from '../../../../../src/shared/components/spinner.js';
import { EmptyState } from '../../../../../src/shared/components/empty-state.js';
import { setPageTitle } from '../../../../../src/shared/page-title.js';
import { getGame, getGameTags, artworkCoverUrl, setFavorite } from '../../api.js';
import { navigate } from '../../router.js';
import { openAddToListModal } from '../../components/add-to-list-modal.js';
import { isQueued, setQueued } from '../../queue-state.js';
import { openGameReport } from '../../../../../src/shared/components/report-game-panel.js';
import { openSuggestGame, suggestFeatureEnabled } from '../../../../../src/shared/components/suggest-game-panel.js';
import { buildShareButton } from '../../../../../src/client/modules/social-share.js';
import { state } from '../../state.js';
import { achievementSharePayload, scoreSharePayload } from './share-payloads.js';
import { installArtworkCoverRecovery } from '../../../../../src/shared/components/artwork-image.js';
import { systemTag } from '../../../../../src/shared/system-label.js';
import { libraryPlayUrl } from '../../play-url.js';
import { releaseBadgeNode, releaseBadgeSuffixNode } from '../../../../../src/shared/components/release-badge.js';
import { mountSuperunoRecords } from '../../../../../src/client/superuno/records.js';

/**
 * Game detail screen — /library/g/:system/:slug
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────┐
 *   │  [← Back]                                                │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  ┌────────┐  Title              [▶ Play]                │
 *   │  │ cover  │  1993 · Action · Konami                     │
 *   │  │  img   │  ★ 7.4 / 10                                │
 *   │  └────────┘                                              │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  Description paragraph...                                │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  Play stats: 2h 15m played · 12 sessions · Last: Apr 14 │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  Achievements (23/45 · 180/400 pts)                      │
 *   │  [✓ Beat Stage 1] [✗ No-Death Run] [✓ Find Secret]...  │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  Variants: USA · Japan · Europe                          │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  Related: [tile] [tile] [tile] [tile]                    │
 *   └──────────────────────────────────────────────────────────┘
 */
export async function render(params, { root, isCurrent = () => true }) {
  empty(root);

  const { system, slug } = params;
  root.appendChild(Spinner({ label: 'Loading game…' }));

  return loadGame(system, slug, root, isCurrent);
}

async function loadGame(system, slug, root, isCurrent) {
  let data;
  try {
    data = await getGame(system, slug);
    if (!isCurrent()) return false;
  } catch (err) {
    if (!isCurrent()) return false;
    empty(root);
    setPageTitle(err.status === 404 ? 'Game not found' : 'Game unavailable');
    root.appendChild(EmptyState({
      title: err.status === 404 ? 'Game not found' : 'Couldn\'t load game',
      message: err.status === 404
        // The tag's own internal space becomes non-breaking so "ATARI 2600"
        // can't split across a line break (matches report-game-panel.js /
        // creator-clips.js / play-together.js's prose-sentence handling).
        ? `No game matched "${slug}" in ${systemTag(system).replace(' ', ' ')}.`
        : 'The server returned an error. Try again in a moment.',
      action: h('a', { class: 'lib-btn', href: '/library' }, '← Back to library'),
      headingLevel: 'h1',
    }));
    return false;
  }

  const { game, variants, achievements, user_stats, related } = data;
  setPageTitle(game.title);
  const me = state.get().user;
  empty(root);

  const screen = h('section', { class: 'lib-game-screen' });

  // ── Back link ──────────────────────────────────────────────────────
  const qs = new URLSearchParams(window.location.search);
  const ref = qs.get('ref');
  const refSys = qs.get('sys');
  let backHref = '/library';
  let backLabel = '← Library';
  if (ref === 'top100' && refSys) {
    const sysLabels = { atari2600: 'Atari', nes: 'NES', genesis: 'Genesis', gameboy: 'Game Boy', snes: 'SNES', gba: 'GBA' };
    backHref = `/top100/${refSys}`;
    backLabel = `← ${sysLabels[refSys] || systemTag(refSys)} Top 100`;
  }
  screen.appendChild(
    h('a', { class: 'lib-game-back', href: backHref }, backLabel)
  );

  // ── Hero: cover + title + metadata + play button ───────────────────
  const coverSrc = game.cover_url
    ? artworkCoverUrl(game.id)
    : '/library/public/placeholder-boxart.svg';

  const coverImg = h('img', {
    class: 'lib-game-cover',
    src: coverSrc,
    alt: `${game.title} cover art`,
    draggable: 'false',
  });
  if (game.cover_url) installArtworkCoverRecovery(coverImg, () => {
    coverImg.src = '/library/public/placeholder-boxart.svg';
  });

  const factBits = [];
  if (game.release_year) factBits.push(String(game.release_year));
  if (game.genres?.length) factBits.push(game.genres.join(', '));
  if (game.developer) factBits.push(game.developer);
  if (game.publisher && game.publisher !== game.developer) factBits.push(game.publisher);

  const ratingEl = game.rating != null
    ? h('div', { class: `lib-game-rating ${game.rating >= 7 ? 'good' : game.rating >= 5 ? 'mid' : 'low'}` },
        h('span', { class: 'lib-game-rating-num' }, game.rating.toFixed(1)),
        h('span', { class: 'lib-game-rating-label' }, ' / 10'),
      )
    : null;

  const defaultVariant = variants[0];
  const playUrl = defaultVariant
    ? libraryPlayUrl(game.system_id, defaultVariant.filename,{solo:game.verified_score_contract!=='superuno-match-points-v1'})
    : null;

  screen.appendChild(
    h('div', { class: 'lib-game-hero' },
      coverImg,
      h('div', { class: 'lib-game-hero-info' },
        h('h1', { id: 'lib-game-title', class: 'lib-game-title' },
          game.title, releaseBadgeSuffixNode(game)),
        releaseBadgeNode(game),
        factBits.length
          ? h('p', { class: 'lib-game-facts' }, factBits.join(' · '))
          : null,
        ratingEl,
        h('div', { class: 'lib-game-hero-actions' },
          playUrl
            ? h('a', {
                class: 'lib-btn lib-btn-primary lib-game-play-btn',
                href: playUrl,
              }, game.verified_score_contract==='superuno-match-points-v1'?'Open multiplayer':'▶  Play')
            : null,
          // Add-to-list: opens a modal showing the user's lists with
          // toggles for whether this game is in each. Gated on game.id
          // because lists link to games, not raw ROMs. Anonymous users
          // see a sign-in hint inside the modal (handled there).
          game.id
            ? h('button', {
                class: 'lib-btn',
                type: 'button',
                onclick: () => openAddToListModal({ gameId: Number(game.id), title: game.title }),
              }, '+ Add to list')
            : null,
          // One-click queue toggle — the queue's whole point is no
          // modal. Optimistic via queue-state, 401 nudges via title.
          game.id ? buildQueueButton(game) : null,
          // Favorite toggle — operates on the primary variant (the
          // `[!]` verified dump when available, first otherwise —
          // server sorts variants that way). Optimistic update with
          // revert on server failure. Hidden when there's no ROM to
          // favorite (unmatched games, anonymous users without a
          // session).
          buildFavoriteButton(variants[0]),
          // Suggest-to-a-friend — flag-gated (launch-dark), so the button
          // reveals itself only once the feature check resolves true. Needs
          // a launchable file, hence the defaultVariant gate.
          defaultVariant
            ? h('button', {
                class: 'lib-btn hidden',
                type: 'button',
                title: 'Suggest this game to a friend',
                'aria-label': `Suggest ${game.title} to a friend`,
                ref: (el) => {
                  suggestFeatureEnabled().then((enabled) => {
                    if (enabled) el.classList.remove('hidden');
                  });
                },
                onclick: () => openSuggestGame({
                  game_id: game.id != null ? Number(game.id) : null,
                  system_id: game.system_id,
                  rom_filename: defaultVariant.filename,
                  rom_hash: defaultVariant.hash || null,
                  game_title: game.title,
                }),
              }, '💡 Suggest')
            : null,
          variants.length > 1
            ? h('span', { class: 'lib-game-variant-count' },
                `${variants.length} variants`)
            : null,
          h('button', {
            class: 'lib-game-report-btn',
            type: 'button',
            title: 'Report a problem with this game',
            'aria-label': 'Report a problem with this game',
            onclick: () => openGameReport({
              game_id: game.id != null ? Number(game.id) : null,
              system_id: game.system_id,
              rom_hash: defaultVariant?.hash || null,
              rom_filename: defaultVariant?.filename || null,
              game_title: game.title,
            }),
          }, '🚩'),
        ),
      ),
    )
  );

  // ── Description ────────────────────────────────────────────────────
  if(game.verified_score_contract==='superuno-match-points-v1'){
    const records=mountSuperunoRecords({current:isCurrent,account:()=>state.get().user?.id??null,
      // Route changes notify too, so a detached view releases its subscription.
      subscribeAccount:callback=>state.subscribe(value=>value,callback)});screen.append(records.element);
  }
  if (game.description) {
    screen.appendChild(
      h('div', { class: 'lib-game-section' },
        h('h2', { class: 'lib-game-section-title' }, 'About'),
        h('p', { class: 'lib-game-description' }, game.description),
      )
    );
  }

  // ── Play stats ─────────────────────────────────────────────────────
  if (user_stats) {
    const statBits = [];
    if (user_stats.play_time_seconds > 0) statBits.push(formatDuration(user_stats.play_time_seconds));
    if (user_stats.session_count > 0) statBits.push(`${user_stats.session_count} session${user_stats.session_count === 1 ? '' : 's'}`);
    if (user_stats.last_played_at) statBits.push(`Last played ${formatDate(user_stats.last_played_at)}`);
    const statEls = statBits.map(s => h('div', { class: 'lib-game-stat' }, s));
    if (user_stats.high_score != null) {
      const stat = h('div', { class: 'lib-game-stat' }, `High score: ${user_stats.high_score.toLocaleString()}`);
      // Own score with a live rank → share entry point. The rank is only
      // served for score-supported games, so an offered button always
      // resolves to a real /share/score card.
      const share = scoreSharePayload(me, game, user_stats);
      if (share) {
        stat.appendChild(buildShareButton(share, { className: 'lib-share-btn', label: 'Share your high score' }));
      }
      statEls.push(stat);
    }

    if (statEls.length) {
      screen.appendChild(
        h('div', { class: 'lib-game-section lib-game-stats' },
          h('h2', { class: 'lib-game-section-title' }, 'Your stats'),
          h('div', { class: 'lib-game-stats-grid' }, ...statEls),
        )
      );
    }
  }

  // ── Achievements ───────────────────────────────────────────────────
  if (achievements.total > 0) {
    const pctLabel = achievements.total > 0
      ? `${Math.round((achievements.unlocked / achievements.total) * 100)}%`
      : '';

    screen.appendChild(
      h('div', { class: 'lib-game-section-achievements lib-game-section' },
        h('h2', { class: 'lib-game-section-title' },
          'Achievements',
          h('span', { class: 'lib-game-section-badge' },
            `${achievements.unlocked}/${achievements.total} · ${pctLabel} · ${achievements.unlocked_points}/${achievements.total_points} pts`),
        ),
        h('div', { class: 'lib-game-ach-bar' },
          h('div', { class: 'lib-game-ach-bar-fill', style: { width: pctLabel } }),
        ),
        h('div', { class: 'lib-game-ach-list' },
          ...achievements.list.map(a => {
            const rarityBadge = a.rarity_tier && a.rarity_percent > 0
              ? h('span', { class: `rarity-badge rarity-${a.rarity_tier}` },
                  `${a.rarity_percent}% of players`)
              : null;
            const pts = h('span', { class: 'lib-game-ach-pts' }, `${a.points} pts`);
            // Share button lives inside the pts cell so the 3-column row
            // grid stays intact; own unlocked achievements only (the
            // payload builder returns null for everything else).
            const share = achievementSharePayload(me, game, a);
            if (share) {
              pts.appendChild(buildShareButton(share, { className: 'lib-share-btn', label: `Share achievement: ${a.name}` }));
            }
            return h('div', { class: `lib-game-ach ${a.unlocked ? 'unlocked' : 'locked'}` },
              h('div', { class: 'lib-game-ach-icon' }, a.icon || (a.unlocked ? '✓' : '○')),
              h('div', { class: 'lib-game-ach-info' },
                h('strong', null, a.name),
                a.description ? h('p', null, a.description) : null,
                rarityBadge,
              ),
              pts,
            );
          }),
        ),
      )
    );
  }

  // ── Genres + Tags ───────────────────────────────────────────────────
  // Genres come from the games.genres[] column (ingestion pipeline).
  // Tags come from library_tags via the /api/library/games/:id/tags endpoint.
  // Both are shown as clickable chips — clicking filters the index by that value.
  const genreChips = (game.genres || []).map(g =>
    h('a', {
      class: 'lib-game-tag-chip lib-game-tag-genre',
      href: `/library?genre=${encodeURIComponent(g)}`,
      title: `Filter by genre: ${g}`,
    }, g)
  );

  // Fetch tags async and append when ready
  const tagContainer = h('div', { class: 'lib-game-tags-row' }, ...genreChips);
  if (genreChips.length > 0 || game.id) {
    screen.appendChild(
      h('div', { class: 'lib-game-section lib-game-tags-section' },
        h('h2', { class: 'lib-game-section-title' }, 'Tags'),
        tagContainer,
      )
    );
  }
  // Tags load async — append to the same row
  if (game.id) {
    getGameTags(game.id).then(tags => {
      for (const tag of tags) {
        // Skip genre-kind tags that duplicate the genres chips above
        if (tag.kind === 'genre' && genreChips.length > 0) continue;
        tagContainer.appendChild(
          h('a', {
            class: `lib-game-tag-chip lib-game-tag-${tag.kind}`,
            href: `/library?tag=${encodeURIComponent(tag.slug)}`,
            title: `Filter by ${tag.kind}: ${tag.label}`,
          }, tag.label)
        );
      }
    }).catch(() => { /* non-fatal */ });
  }

  // ── Variants ───────────────────────────────────────────────────────
  if (variants.length > 1) {
    screen.appendChild(
      h('div', { class: 'lib-game-section' },
        h('h2', { class: 'lib-game-section-title' }, 'ROM variants'),
        h('div', { class: 'lib-game-variants' },
          ...variants.map(v => {
            return h('a', {
              class: 'lib-game-variant-chip',
              href: libraryPlayUrl(game.system_id, v.filename),
              title: v.filename,
            },
              h('span', null, v.variant || v.filename),
              releaseBadgeNode(v),
              releaseBadgeSuffixNode(v),
              h('span', { class: 'lib-game-variant-size' }, humanSize(v.size)),
            );
          }),
        ),
      )
    );
  }

  // ── Related games ──────────────────────────────────────────────────
  if (related.length > 0) {
    screen.appendChild(
      h('div', { class: 'lib-game-section' },
        h('h2', { class: 'lib-game-section-title' }, 'Related games'),
        h('div', { class: 'lib-game-related' },
          ...related.map(r => {
            const rCover = r.cover_url ? artworkCoverUrl(r.id) : '/library/public/placeholder-boxart.svg';
            const relatedCover = h('img', {
              class: 'lib-game-related-cover',
              src: rCover,
              alt: '',
              loading: 'lazy',
              draggable: 'false',
            });
            if (r.cover_url) installArtworkCoverRecovery(relatedCover, () => {
              relatedCover.src = '/library/public/placeholder-boxart.svg';
            });
            return h('a', {
              class: 'lib-game-related-card',
              href: `/library/g/${system}/${r.slug}`,
            },
              relatedCover,
              h('span', { class: 'lib-game-related-title' }, r.title),
            );
          }),
        ),
      )
    );
  }

  root.appendChild(screen);
  return true;
}

// ── Formatting helpers ───────────────────────────────────────────────

// Takes a duration in SECONDS (matches the API's play_time_seconds).
// Displays as "Xm played" below an hour, "Xh Ym played" above.
/**
 * Favorite toggle for the game detail hero row. Operates on the
 * primary variant (server sorts `[!]` first, then id asc). Optimistic
 * UI — flip the ★/☆ immediately, revert on server failure. Null
 * return when there's no variant (unmatched game) — the caller
 * filters null children out.
 */
function buildFavoriteButton(primary) {
  if (!primary || !primary.hash || !primary.system_id) return null;
  let favorited = !!primary.is_favorited;
  const btn = h('button', {
    class: `lib-btn lib-game-fav-btn${favorited ? ' active' : ''}`,
    type: 'button',
    title: favorited ? 'Unfavorite' : 'Add to favorites',
    'aria-pressed': favorited ? 'true' : 'false',
  }, favorited ? '★ Favorited' : '☆ Favorite');
  btn.addEventListener('click', async () => {
    const next = !favorited;
    favorited = next;
    btn.classList.toggle('active', next);
    btn.setAttribute('aria-pressed', next ? 'true' : 'false');
    btn.title = next ? 'Unfavorite' : 'Add to favorites';
    btn.textContent = next ? '★ Favorited' : '☆ Favorite';
    try {
      await setFavorite(primary.system_id, primary.hash, next);
    } catch (err) {
      // Revert on failure — 401 = signed out, anything else transient.
      favorited = !next;
      btn.classList.toggle('active', !next);
      btn.setAttribute('aria-pressed', !next ? 'true' : 'false');
      btn.title = !next ? 'Unfavorite' : 'Add to favorites';
      btn.textContent = !next ? '★ Favorited' : '☆ Favorite';
      if (err?.status === 401) btn.title = 'Sign in to favorite';
      console.warn('[game] favorite toggle failed:', err);
    }
  });
  return btn;
}

/**
 * "+ Queue" / "✓ Queued" toggle for the hero row. Same optimistic
 * flip-then-revert shape as the favorite button, but state lives in
 * the shared queue-state set so tiles and this button stay in sync.
 */
function buildQueueButton(game) {
  const gameId = Number(game.id);
  let inQueue = isQueued(gameId);
  const btn = h('button', {
    class: `lib-btn lib-game-queue-btn${inQueue ? ' active' : ''}`,
    type: 'button',
    'aria-pressed': inQueue ? 'true' : 'false',
    title: inQueue ? 'Remove from your queue' : 'Add to your queue',
  }, inQueue ? '✓ Queued' : '+ Queue');
  const apply = () => {
    btn.classList.toggle('active', inQueue);
    btn.setAttribute('aria-pressed', inQueue ? 'true' : 'false');
    btn.title = inQueue ? 'Remove from your queue' : 'Add to your queue';
    btn.textContent = inQueue ? '✓ Queued' : '+ Queue';
  };
  // Direct detail-page loads race boot's fire-and-forget loadQueue(): the
  // isQueued() read above can run before the set hydrates, leaving a queued
  // game rendered "+ Queue" forever. Re-check once when hydration lands
  // ({once} keeps the detached-button leak bounded to a single fire).
  document.addEventListener('library:queue-loaded', () => {
    inQueue = isQueued(gameId);
    apply();
  }, { once: true });
  btn.addEventListener('click', async () => {
    const next = !inQueue;
    inQueue = next;
    apply();
    try {
      await setQueued(gameId, next);
    } catch (err) {
      inQueue = !next;
      apply();
      if (err?.status === 401) btn.title = 'Sign in to use your queue';
      console.warn('[game] queue toggle failed:', err);
    }
  });
  return btn;
}

function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m played`;
  const hours = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0 ? `${hours}h ${rem}m played` : `${hours}h played`;
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function humanSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  const kb = Number(bytes) / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
